import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../constants/appConstants';
import { getOrgBrand, OrgBrand } from '../config/orgBrand';
import { useAuth } from './AuthContext';
import { EmployeeOrg, getEmployeeGroup, getThemeOrg } from '../utils/employeeGroup';

interface OrgThemeContextValue {
  org: EmployeeOrg;
  themeOrg: 'elink' | 'phishingbox';
  brand: OrgBrand;
  loading: boolean;
  resolveOrgFromName: (fullName: string) => EmployeeOrg;
}

const defaultValue: OrgThemeContextValue = {
  org: 'elink',
  themeOrg: 'elink',
  brand: getOrgBrand('elink'),
  loading: true,
  resolveOrgFromName: () => 'elink',
};

const OrgThemeContext = createContext<OrgThemeContextValue>(defaultValue);

export const OrgThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [org, setOrg] = useState<EmployeeOrg>('elink');
  const [elinkEmployeeNames, setElinkEmployeeNames] = useState<Set<string>>(new Set());
  const [orgResolved, setOrgResolved] = useState(false);

  const loading = isAuthenticated && !orgResolved;

  const resolveOrgFromName = useCallback(
    (fullName: string) => getEmployeeGroup(fullName, elinkEmployeeNames),
    [elinkEmployeeNames]
  );

  useEffect(() => {
    if (!isAuthenticated) {
      setOrg('elink');
      setOrgResolved(true);
      document.documentElement.setAttribute('data-org', 'elink');
      return;
    }

    let cancelled = false;
    setOrgResolved(false);

    const loadOrg = async () => {
      try {
        const [userResponse, employeesResponse] = await Promise.all([
          axios.get<{ name?: string }>(`${API_URL}/intranet/me`, { withCredentials: true }),
          axios.get<Array<{ name: string; department?: string }>>(`${API_URL}/employees`, {
            withCredentials: true,
          }),
        ]);

        if (cancelled) return;

        const names = new Set(
          employeesResponse.data
            .filter(
              (employee) => (employee.department || '').toLowerCase() !== 'phishingbox'
            )
            .map((employee) => employee.name.trim().toLowerCase())
        );
        setElinkEmployeeNames(names);

        const fullName = userResponse.data?.name || '';
        const resolvedOrg = fullName ? getEmployeeGroup(fullName, names) : 'elink';
        setOrg(resolvedOrg);
      } catch {
        if (!cancelled) {
          setOrg('elink');
        }
      } finally {
        if (!cancelled) {
          setOrgResolved(true);
        }
      }
    };

    loadOrg();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  const themeOrg = getThemeOrg(org);
  const brand = getOrgBrand(org);

  useEffect(() => {
    document.documentElement.setAttribute('data-org', themeOrg);

    const metaTheme = document.querySelector('meta[name="theme-color"]');
    if (metaTheme) {
      metaTheme.setAttribute('content', brand.themeColor);
    }

    return () => {
      document.documentElement.setAttribute('data-org', 'elink');
    };
  }, [themeOrg, brand.themeColor]);

  return (
    <OrgThemeContext.Provider
      value={{
        org,
        themeOrg,
        brand,
        loading,
        resolveOrgFromName,
      }}
    >
      {children}
    </OrgThemeContext.Provider>
  );
};

export const useOrgTheme = (): OrgThemeContextValue => {
  const context = useContext(OrgThemeContext);
  if (context === undefined) {
    throw new Error('useOrgTheme must be used within an OrgThemeProvider');
  }
  return context;
};

export default OrgThemeContext;
